import { saveAs } from 'file-saver';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { centsToXlsxNumber, applyCurrencyFormat } from '../../../helpers/excelExportHelpers';
import groupCommissionItems from './groupCommissionItems';

/**
 * Export commission report data to XLSX file
 * @param {object} reportData - The report data from the API
 * @param {string} dateFrom - Start date string
 * @param {string} dateTo - End date string
 */
export const exportCommissionReportToXlsx = async (reportData, dateFrom, dateTo) => {
    const XLSX = await import('xlsx');
    const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const sheetData = [];

    // Header
    sheetData.push(['Commission Report']);
    sheetData.push([
        `Date Range: ${new Date(dateFrom + 'T12:00:00').toLocaleDateString()} - ${new Date(dateTo + 'T12:00:00').toLocaleDateString()}`,
    ]);
    sheetData.push([
        `Total Line Amount: ${formatCentsToUsd(reportData.totalLineAmount || 0)}`,
        `Total Commission: ${formatCentsToUsd(reportData.totalCommission || 0)}`,
    ]);
    sheetData.push([]);

    const headers = [
        'Booking ID',
        'Booking Status',
        'Show',
        'Agent',
        'Contact',
        'Email',
        'Customers',
        'Customer Count',
        'Show Date',
        'Commission Group',
        'Line Total',
        'Commission',
    ];

    const groups = groupCommissionItems(reportData.items || []);

    for (const group of groups) {
        // Group header
        sheetData.push([`Commission Group: ${group.commissionGroupName}`]);
        sheetData.push(headers);

        // Data rows
        group.items.forEach((item) => {
            sheetData.push([
                item.bookingId,
                item.bookingStatus || '',
                item.showTitle,
                item.agentName,
                item.contact,
                item.email,
                item.customers,
                item.customerCount,
                item.availability,
                item.commissionGroupName || '',
                centsToXlsxNumber(item.lineTotal),
                centsToXlsxNumber(item.commissionAmount),
            ]);
        });

        sheetData.push([
            'Subtotal',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            centsToXlsxNumber(group.lineTotal),
            centsToXlsxNumber(group.commissionAmount),
        ]);
        sheetData.push([]);
    }

    sheetData.push([
        'Grand Total',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        centsToXlsxNumber(reportData.totalLineAmount),
        centsToXlsxNumber(reportData.totalCommission),
    ]);

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    ws['!cols'] = [
        { wch: 12 }, // Booking ID
        { wch: 15 }, // Booking Status
        { wch: 25 }, // Show
        { wch: 20 }, // Agent
        { wch: 25 }, // Contact
        { wch: 30 }, // Email
        { wch: 25 }, // Customers
        { wch: 15 }, // Customer Count
        { wch: 20 }, // Show Date
        { wch: 20 }, // Commission Group
        { wch: 12 }, // Line Total
        { wch: 12 }, // Commission
    ];

    // Apply currency format to Line Total (col 10) and Commission (col 11)
    applyCurrencyFormat(XLSX, ws, sheetData, [10, 11]);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Commission Report');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: fileType });

    const filename = `commission-report-${dateFrom}-to-${dateTo}${fileExtension}`;
    saveAs(file, filename);
};

export default exportCommissionReportToXlsx;
